import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { isApiError } from '@/lib/api'
import { cn } from '@/lib/cn'

type ErrorStateProps = {
  /** Anything thrown — ApiError, Error, or an unknown value. */
  error: unknown
  title?: string
  onRetry?: () => void
  /** Inline variant for a panel inside a page rather than a whole-page failure. */
  compact?: boolean
  className?: string
}

/**
 * The one way this app reports a failed request. Shows the server's message (which is
 * written to be safe for staff to read) plus the trace id, so a bug report can be tied
 * to a server log line without a screenshot of the console.
 */
export function ErrorState({ error, title, onRetry, compact = false, className }: ErrorStateProps) {
  const apiError = isApiError(error) ? error : null

  const heading =
    title ??
    (apiError?.isNetwork
      ? 'Cannot reach the server'
      : apiError?.isForbidden
        ? 'You do not have access'
        : apiError?.isNotFound
          ? 'Not found'
          : 'Something went wrong')

  const message = apiError
    ? apiError.message
    : error instanceof Error && error.message
      ? error.message
      : 'An unexpected error occurred.'

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-danger-500/30 bg-danger-50/60 text-center',
        compact ? 'px-4 py-6' : 'px-4 py-12',
        className,
      )}
    >
      <span
        className="flex size-9 items-center justify-center rounded-full bg-danger-100 text-danger-700"
        aria-hidden="true"
      >
        <AlertCircle className="size-5" />
      </span>

      <div className="space-y-1">
        <p className="font-medium text-ink">{heading}</p>
        <p className="mx-auto max-w-md text-ink-muted">{message}</p>
        {apiError?.traceId ? (
          <p className="text-xs text-ink-subtle">
            Reference: <code className="font-mono">{apiError.traceId}</code>
          </p>
        ) : null}
      </div>

      {onRetry ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          leadingIcon={<RefreshCw className="size-4" aria-hidden="true" />}
        >
          Try again
        </Button>
      ) : null}
    </div>
  )
}
